import {
    IPoint,
    ISize,
    IRectangle,
    IEllipse,
    isPoint,
    isSize,
    isRectangle,
    isEllipse,
} from '../../geometry/geometry2d';
import { assertNever } from '../../common/lang/errors';

export enum PrintUnits {
    points = 'pt',
    pixels = 'px',
    inches = 'in',
    millimetres = 'mm',
}

export const POINTS_PER_INCH = 72;
export const PIXELS_PER_INCH = 96;
export const MM_PER_INCH = 25.4;

type ConvertUnitsValue = IEllipse | IPoint | IRectangle | ISize | number;

export function convertUnits(value: number, fromUnits: PrintUnits, toUnits: PrintUnits): number;
export function convertUnits(value: IRectangle, fromUnits: PrintUnits, toUnits: PrintUnits): IRectangle;
export function convertUnits(value: ISize, fromUnits: PrintUnits, toUnits: PrintUnits): ISize;
export function convertUnits(value: IEllipse, fromUnits: PrintUnits, toUnits: PrintUnits): IEllipse;
export function convertUnits(value: IPoint, fromUnits: PrintUnits, toUnits: PrintUnits): IPoint;

export function convertUnits(value: ConvertUnitsValue, fromUnits: PrintUnits, toUnits: PrintUnits): ConvertUnitsValue {
    /* eslint-disable @typescript-eslint/consistent-type-assertions */
    if (isRectangle(value)) {
        return {
            height: convertUnits(value.height, fromUnits, toUnits),
            width: convertUnits(value.width, fromUnits, toUnits),
            x: convertUnits(value.x, fromUnits, toUnits),
            y: convertUnits(value.y, fromUnits, toUnits),
        } as IRectangle;
    } else if (isEllipse(value)) {
        return {
            radiusX: convertUnits(value.radiusX, fromUnits, toUnits),
            radiusY: convertUnits(value.radiusY, fromUnits, toUnits),
            x: convertUnits(value.x, fromUnits, toUnits),
            y: convertUnits(value.y, fromUnits, toUnits),
        } as IEllipse;
    } else if (isPoint(value)) {
        return {
            x: convertUnits(value.x, fromUnits, toUnits),
            y: convertUnits(value.y, fromUnits, toUnits),
        } as IPoint;
    } else if (isSize(value)) {
        return {
            height: convertUnits(value.height, fromUnits, toUnits),
            width: convertUnits(value.width, fromUnits, toUnits),
        } as ISize;
    }
    /* eslint-enable @typescript-eslint/consistent-type-assertions */

    /**
     * No change? No problems
     * This check could be moved to the top, but prefer it here so that
     * result is new instance rather than same instance passed in
     */
    if (fromUnits === toUnits) {
        return value;
    }

    let inches: number;

    switch (fromUnits) {
        case PrintUnits.points:
            inches = value / POINTS_PER_INCH;
            break;

        case PrintUnits.pixels:
            inches = value / PIXELS_PER_INCH;
            break;

        case PrintUnits.inches:
            inches = value;
            break;

        case PrintUnits.millimetres:
            inches = value / MM_PER_INCH;
            break;

        default:
            throw assertNever(fromUnits);
    }

    switch (toUnits) {
        case PrintUnits.points:
            return inches * POINTS_PER_INCH;

        case PrintUnits.pixels:
            return inches * PIXELS_PER_INCH;

        case PrintUnits.inches:
            return inches;

        case PrintUnits.millimetres:
            return inches * MM_PER_INCH;

        default:
            throw assertNever(toUnits);
    }
}
